const CART_DRAWER_WALLET_FILTERED_ATTR = 'data-single-wallet-filtered';

const CART_DRAWER_WALLET_SELECTORS = {
  apple_pay: 'shopify-apple-pay-button',
  shop_pay: 'shop-pay-wallet-button',
  google_pay: 'shopify-google-pay-button',
  paypal: 'shopify-paypal-button',
};

function isCartDrawerApplePayLikelyAvailable() {
  const ua = navigator.userAgent;
  const isAppleDevice = /iPhone|iPad|iPod|Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
  const hasApplePayAPI = !!(window.ApplePaySession && ApplePaySession.canMakePayments);
  return (isAppleDevice && isSafari) || hasApplePayAPI;
}

function getCartDrawerWalletPriority() {
  if (isCartDrawerApplePayLikelyAvailable()) {
    return ['apple_pay', 'shop_pay', 'paypal', 'google_pay'];
  }
  return ['shop_pay', 'paypal', 'google_pay'];
}

function parseCartDrawerWalletConfigs(container) {
  const walletConfigs = container.getAttribute('wallet-configs');
  if (!walletConfigs) return null;
  try {
    return JSON.parse(walletConfigs);
  } catch (e) {
    console.warn('Could not parse wallet-configs:', e);
    return null;
  }
}

function pickCartDrawerWallet(configs) {
  if (!configs?.length) return null;
  const priority = getCartDrawerWalletPriority();
  return priority.find((name) => configs.some((cfg) => cfg.name === name)) || null;
}

function queryCartDrawerWalletButtons(container, selector) {
  const nodes = [...container.querySelectorAll(selector)];
  if (container.shadowRoot) {
    nodes.push(...container.shadowRoot.querySelectorAll(selector));
  }
  return nodes;
}

function hideCartDrawerWalletButtons(container, chosenName) {
  Object.entries(CART_DRAWER_WALLET_SELECTORS).forEach(([name, selector]) => {
    queryCartDrawerWalletButtons(container, selector).forEach((btn) => {
      const show = name === chosenName;
      btn.removeAttribute('hidden');
      if (show) {
        btn.removeAttribute('data-cart-drawer-wallet-hidden');
        btn.style.removeProperty('display');
      } else {
        btn.setAttribute('data-cart-drawer-wallet-hidden', 'true');
        btn.style.setProperty('display', 'none', 'important');
      }
    });
  });

  styleCartDrawerWalletShadowGrid(container, chosenName);
}

function styleCartDrawerWalletShadowGrid(container, chosenName) {
  const root = container.shadowRoot;
  if (!root) return;

  let style = root.querySelector('[data-single-wallet-style]');
  if (!style) {
    style = document.createElement('style');
    style.setAttribute('data-single-wallet-style', 'true');
    root.appendChild(style);
  }

  const chosenSelector = CART_DRAWER_WALLET_SELECTORS[chosenName];
  const hideRule = chosenSelector
    ? Object.values(CART_DRAWER_WALLET_SELECTORS)
        .filter((sel) => sel !== chosenSelector)
        .map((sel) => `${sel}, .wallet-cart-button-container:has(${sel})`)
        .join(', ')
    : '.wallet-cart-button-container';

  style.textContent = `
    .wallet-cart-grid {
      display: flex !important;
      flex-direction: row !important;
      gap: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    .wallet-cart-button-container {
      flex: 1 1 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
    }
    ${hideRule} {
      display: none !important;
    }
  `;
}

/**
 * Shopify's cart wallet grid lives in a closed shadow root, so we cannot hide
 * spare grid cells with CSS. Re-mount the host with a single wallet-config instead.
 */
function replaceCartDrawerSingleWalletConfig(container, chosenName) {
  if (!container.parentNode) return false;

  const configs = parseCartDrawerWalletConfigs(container);
  if (!configs?.length) return false;

  const filteredConfigs = configs.filter((cfg) => cfg.name === chosenName);
  if (filteredConfigs.length !== 1) return false;

  const filteredJson = JSON.stringify(filteredConfigs);
  if (JSON.stringify(configs) === filteredJson) return false;

  const newEl = document.createElement('shopify-accelerated-checkout-cart');
  for (const attr of container.attributes) {
    if (attr.name === 'wallet-configs') {
      newEl.setAttribute('wallet-configs', filteredJson);
    } else if (attr.name !== CART_DRAWER_WALLET_FILTERED_ATTR) {
      newEl.setAttribute(attr.name, attr.value);
    }
  }
  newEl.setAttribute(CART_DRAWER_WALLET_FILTERED_ATTR, chosenName);

  container.parentNode.replaceChild(newEl, container);
  return true;
}

function updateCartDrawerExpressColumnVisibility(container, chosenName) {
  const expressColumn = container.closest('[data-cart-drawer-express-checkout]');
  if (!expressColumn) return;

  const isEmpty = document.querySelector('cart-drawer')?.classList.contains('is-empty');
  expressColumn.classList.toggle('cart-drawer__express-checkout--hidden', isEmpty || !chosenName);
}

function setCartDrawerExpressWalletsDisabled(disabled) {
  const drawer = document.querySelector('cart-drawer');
  const expressColumn = drawer?.querySelector('[data-cart-drawer-express-checkout]');
  if (!expressColumn) return;

  expressColumn.classList.toggle('cart-drawer__express-checkout--disabled', disabled);

  expressColumn.querySelectorAll('shopify-accelerated-checkout-cart').forEach((container) => {
    container.toggleAttribute('aria-disabled', disabled);
    if (disabled) {
      container.setAttribute('inert', '');
    } else {
      container.removeAttribute('inert');
      container.removeAttribute('aria-disabled');
    }

    queryCartDrawerWalletButtons(container, 'shopify-apple-pay-button').forEach((btn) => {
      btn.toggleAttribute('disabled', disabled);
      btn.style.setProperty('pointer-events', disabled ? 'none' : '', 'important');
    });
  });

  expressColumn.querySelectorAll('[data-cart-drawer-apple-pay-placeholder]').forEach((btn) => {
    btn.toggleAttribute('disabled', disabled);
  });
}

function applyCartDrawerSingleExpressCheckout(container) {
  if (!container.closest('cart-drawer, #CartDrawer')) return;

  const configs = parseCartDrawerWalletConfigs(container);
  if (!configs?.length) {
    updateCartDrawerExpressColumnVisibility(container, null);
    return;
  }

  const chosen = pickCartDrawerWallet(configs);
  if (!chosen) {
    updateCartDrawerExpressColumnVisibility(container, null);
    return;
  }

  if (replaceCartDrawerSingleWalletConfig(container, chosen)) {
    return;
  }

  const applyVisibility = () => {
    hideCartDrawerWalletButtons(container, chosen);
    updateCartDrawerExpressColumnVisibility(container, chosen);
  };

  applyVisibility();
  [100, 400, 800, 1200].forEach((delay) => setTimeout(applyVisibility, delay));
}

function scanCartDrawerExpressCheckout(root = document) {
  if (root.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') {
    applyCartDrawerSingleExpressCheckout(root);
    return;
  }

  const scope =
    root === document ? document.querySelector('cart-drawer') : root.closest?.('cart-drawer') || root;

  scope?.querySelectorAll?.('shopify-accelerated-checkout-cart').forEach(applyCartDrawerSingleExpressCheckout);
}

let cartDrawerExpressCheckoutObserver;

function initCartDrawerExpressCheckout() {
  scanCartDrawerExpressCheckout(document);

  if (cartDrawerExpressCheckoutObserver) return;

  cartDrawerExpressCheckoutObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;

        if (node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') {
          applyCartDrawerSingleExpressCheckout(node);
        } else if (node.querySelector) {
          scanCartDrawerExpressCheckout(node);
        }
      }
    }
  });

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawerExpressCheckoutObserver.observe(cartDrawer, { childList: true, subtree: true });
  }
}

window.initCartDrawerExpressCheckout = initCartDrawerExpressCheckout;

/**
 * Keeps drawer checkout in sync when the cart is emptied via AJAX partial updates
 * (checkout markup lives outside the blocks that get replaced).
 */
function syncCartDrawerCheckoutEmptyState(isEmpty) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  drawer.classList.toggle('is-empty', isEmpty);
  drawer.classList.remove('cart-drawer--empty-express-wallets');

  const footer = drawer.querySelector('.drawer__footer');
  if (footer) {
    footer.classList.toggle('drawer__footer--empty-cart', isEmpty);
  }

  const footerProgress = drawer.querySelector('.cart-drawer__footer-progress');
  if (footerProgress) {
    footerProgress.classList.toggle('hidden', isEmpty);
  }

  const checkoutButton = drawer.querySelector('#CartDrawer-Checkout');
  if (checkoutButton) {
    checkoutButton.toggleAttribute('disabled', isEmpty);
  }

  const ctas = drawer.querySelector('.cart__ctas');
  if (ctas) {
    ctas.classList.toggle('cart__ctas--empty-cart', isEmpty);
  }

  const expressCheckout = drawer.querySelector('[data-cart-drawer-express-checkout]');
  if (expressCheckout) {
    expressCheckout.classList.toggle('cart-drawer__express-checkout--hidden', isEmpty);
    expressCheckout.classList.remove('cart-drawer__express-checkout--disabled');
    expressCheckout.querySelector('[data-cart-drawer-apple-pay-placeholder]')?.remove();
  }

  if (!isEmpty) {
    setCartDrawerExpressWalletsDisabled(false);
    initCartDrawerExpressCheckout();
  }
}

window.syncCartDrawerCheckoutEmptyState = syncCartDrawerCheckoutEmptyState;

const CART_DRAWER_PARTIAL_SELECTORS = ['cart-drawer-items', '.drawer__footer'];

function applyCartDrawerSectionHtml(responseText, selectors = CART_DRAWER_PARTIAL_SELECTORS) {
  const html = new DOMParser().parseFromString(responseText, 'text/html');
  const drawer = document.querySelector('cart-drawer');
  const incomingDrawer = html.querySelector('cart-drawer');
  if (!drawer || !incomingDrawer) return;

  selectors.forEach((selector) => {
    const targetElement = drawer.querySelector(selector);
    const sourceElement = incomingDrawer.querySelector(selector);
    if (targetElement && sourceElement) {
      targetElement.replaceWith(sourceElement);
    }
  });

  drawer.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));

  const incomingEmpty = incomingDrawer.querySelector('.drawer__inner-empty');
  const localInner = drawer.querySelector('.drawer__inner');
  const localEmpty = localInner ? localInner.querySelector('.drawer__inner-empty') : null;

  if (incomingEmpty && localInner) {
    if (localEmpty) {
      localEmpty.replaceWith(incomingEmpty);
    } else {
      const anchor = localInner.querySelector('cart-drawer-items') || localInner.firstElementChild;
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(incomingEmpty, anchor);
      } else {
        localInner.appendChild(incomingEmpty);
      }
    }
  } else if (!incomingEmpty && localEmpty) {
    localEmpty.remove();
  }

  if (typeof window.syncCartDrawerCheckoutEmptyState === 'function') {
    window.syncCartDrawerCheckoutEmptyState(incomingDrawer.classList.contains('is-empty'));
  }
  if (typeof window.initCartDrawerPromoDiscount === 'function') {
    window.initCartDrawerPromoDiscount();
  }
  if (typeof window.initCartDrawerBundleDetails === 'function') {
    window.initCartDrawerBundleDetails();
  }
  if (typeof window.initCartDrawerExpressCheckout === 'function') {
    window.initCartDrawerExpressCheckout();
  }
}

function refreshCartDrawerFooter() {
  return fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
    .then((response) => response.text())
    .then((responseText) => {
      applyCartDrawerSectionHtml(responseText, ['.drawer__footer']);
    })
    .catch((error) => {
      console.error('[CartDrawer] Failed to refresh cart drawer footer', error);
    });
}

window.refreshCartDrawerFooter = refreshCartDrawerFooter;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    observeIcuUpsellMount();
    initCartDrawerPromoDiscount();
    initCartDrawerBundleDetails();
    initCartDrawerExpressCheckout();
    if (this.classList.contains('is-empty')) {
      syncCartDrawerCheckoutEmptyState(true);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, { skipRefresh = false } = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const showDrawer = () => {
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);

          setTimeout(() => {
            // SWIPER HANDLING
            this.freeSampleSwiper = new Swiper('.drawer__freeSample-scroll', {
              loop: true,
              draggable: true,
              slidesPerView: 3,
              spaceBetween: 10,
              breakpoints: {
                300: {
                  spaceBetween: 10,
                  slidesPerView: 3,
                  loop: false,
                  centeredSlides: false
                },
                768: {
                  spaceBetween: 10,
                  slidesPerView: 3,
                  centeredSlides: false,
                  loop: false
                },
                1200: {
                  slidesPerView: 3,
                  spaceBetween: 10,
                  loop: false
                }
              },
              // Navigation arrows
              navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              },
            });
          }, 750);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    };

    if (skipRefresh) {
      showDrawer();
    } else {
      this.refreshContents().finally(showDrawer);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (typeof window.syncCartDrawerCheckoutEmptyState === 'function') {
      window.syncCartDrawerCheckoutEmptyState(false);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open(undefined, { skipRefresh: true });
      this.reInitPaymentButtons();
      initCartDrawerPromoDiscount();
      initCartDrawerBundleDetails();
      initCartDrawerExpressCheckout();
      setTimeout(() => initCartDrawerExpressCheckout(), 600);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  reInitPaymentButtons() {
    try {
      if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
        Shopify.PaymentButton.init();
      }
    } catch (error) {
      console.warn('[CartDrawer] Failed to reinitialize payment buttons', error);
    }
  }

  refreshContents() {
    if (this._refreshInFlight) return this._refreshPromise;

    this._refreshInFlight = true;
    this._refreshPromise = fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((response) => response.text())
      .then((responseText) => {
        applyCartDrawerSectionHtml(responseText);

        return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`, { cache: 'no-store' });
      })
      .then((response) => response.text())
      .then((responseText) => {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon) return;

        cartIcon.innerHTML = this.getSectionInnerHTML(responseText);
        this.reInitPaymentButtons();
      })
      .catch((error) => {
        console.error('[CartDrawer] Failed to refresh cart contents', error);
      })
      .finally(() => {
        this._refreshInFlight = false;
      });

    return this._refreshPromise;
  }
}

customElements.define('cart-drawer', CartDrawer);

function refreshCartDrawerOnPageRestore() {
  document.querySelector('cart-drawer')?.refreshContents();
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    refreshCartDrawerOnPageRestore();
  }
});
/**
 * Moves DCART's injected .scDiscount__container into the promo slot and wires toggle UI.
 */
function mountScDiscountInPromoSlot(drawer) {
  const slot = drawer.querySelector('[data-cart-drawer-promo-slot]');
  if (!slot) return;

  drawer.querySelectorAll('.scDiscount__container').forEach((container) => {
    if (container.closest('[data-cart-drawer-promo-slot]')) return;
    slot.appendChild(container);
  });
}

/**
 * Moves ICU/Nudge upsell (#nudge-offer-drawer-icu) into the footer slot after progress.
 */
function mountIcuUpsellInFooterSlot(drawer) {
  const slot = drawer.querySelector('[data-cart-drawer-upsell-slot]');
  if (!slot || drawer.classList.contains('is-empty')) return;

  const nudgeRoot = document.getElementById('nudge-offer-drawer-icu');
  if (!nudgeRoot || nudgeRoot.parentElement === slot) return;

  slot.appendChild(nudgeRoot);
}

function observeIcuUpsellMount() {
  if (document.documentElement.dataset.icuUpsellMountObserved === 'true') return;
  document.documentElement.dataset.icuUpsellMountObserved = 'true';

  const tryMount = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) mountIcuUpsellInFooterSlot(drawer);
  };

  const observer = new MutationObserver(tryMount);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  tryMount();
}

function syncEmptyCartPromoState(drawer) {
  const isEmpty = drawer.classList.contains('is-empty');
  const promo = drawer.querySelector('[data-cart-drawer-promo]');
  const toggle = drawer.querySelector('.cart-drawer__footer-promo-toggle');
  const panel = drawer.querySelector('#CartDrawer-PromoPanel');
  const iconPlus = drawer.querySelector('.cart-drawer__footer-promo-toggle__icon--plus');
  const iconClose = drawer.querySelector('.cart-drawer__footer-promo-toggle__icon--close');

  if (!toggle || !promo) return;

  if (isEmpty) {
    promo.classList.add('cart-drawer__footer-promo--disabled');
    toggle.disabled = true;
    toggle.setAttribute('aria-disabled', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    iconPlus?.classList.remove('visually-hidden');
    iconClose?.classList.add('visually-hidden');
  } else {
    promo.classList.remove('cart-drawer__footer-promo--disabled');
    toggle.disabled = false;
    toggle.removeAttribute('aria-disabled');
  }
}

function initCartDrawerPromoDiscount() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  mountScDiscountInPromoSlot(drawer);
  mountIcuUpsellInFooterSlot(drawer);
  syncEmptyCartPromoState(drawer);

  if (!drawer.dataset.promoDiscountBound) {
    drawer.dataset.promoDiscountBound = 'true';

    drawer.addEventListener('click', (event) => {
      const toggle = event.target.closest('.cart-drawer__footer-promo-toggle');
      if (!toggle || toggle.disabled || drawer.classList.contains('is-empty')) return;

      const panel = drawer.querySelector('#CartDrawer-PromoPanel');
      const slot = drawer.querySelector('[data-cart-drawer-promo-slot]');
      const iconPlus = drawer.querySelector('.cart-drawer__footer-promo-toggle__icon--plus');
      const iconClose = drawer.querySelector('.cart-drawer__footer-promo-toggle__icon--close');
      if (!panel) return;

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      const nextOpen = !isOpen;

      toggle.setAttribute('aria-expanded', String(nextOpen));
      panel.hidden = !nextOpen;

      iconPlus?.classList.toggle('visually-hidden', nextOpen);
      iconClose?.classList.toggle('visually-hidden', !nextOpen);

      if (nextOpen) {
        mountScDiscountInPromoSlot(drawer);
        mountIcuUpsellInFooterSlot(drawer);
        const input = slot?.querySelector('#code, .sc-cube-code');
        input?.focus();
      }
    });

    const observer = new MutationObserver(() => {
      mountScDiscountInPromoSlot(drawer);
      mountIcuUpsellInFooterSlot(drawer);
      syncEmptyCartPromoState(drawer);
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    observeIcuUpsellMount();
    initCartDrawerPromoDiscount();
    initCartDrawerBundleDetails();
    initCartDrawerExpressCheckout();
  });
} else {
  observeIcuUpsellMount();
  initCartDrawerPromoDiscount();
  initCartDrawerBundleDetails();
  initCartDrawerExpressCheckout();
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.classList.contains('is-empty')) {
    syncCartDrawerCheckoutEmptyState(true);
  }
}

window.initCartDrawerPromoDiscount = initCartDrawerPromoDiscount;

function initCartDrawerBundleDetails() {
  if (typeof window.initCartBundleDetails === 'function') {
    window.initCartBundleDetails();
  }
}

window.initCartDrawerBundleDetails = initCartDrawerBundleDetails;

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
